import { FC, PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Divider } from '@frontend/divider';
import {
  CallInsightSummaryWritebackBanner,
  TSSRecoveredOpportunityBanner,
  TSSUnscheduledBanner,
} from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { PriorityRendererItemWrapper } from '@frontend/priority-renderer';
import { theme } from '@frontend/theme';
import { Accordion, Tabs } from '@frontend/design-system';
import { CIBannerEnum, CIBannerPriorityGroupEnum } from '../../constants';
import { useCallIntelPmsSyncConfig } from '../../hooks/use-call-intel-pms-sync-config';
import { CallIntelTabType } from '../../types';
import { callStatusUtils, generateTrackingId } from '../../utils';
import { CallAnalysis, CallAnalysisProps } from '../call-analysis';
import { CallAnalyzeStatus } from '../call-analyze-status';
import { CallIntelFeedbackBanner } from '../call-intel-feedback-banner';
import { CallSummary, CallSummaryProps } from '../call-summary';
import { CallTranscript, CallTranscriptProps } from '../call-transcript';
import { SummarySyncChip } from '../pms-sync-status';

interface CallIntelAccordionNamespace extends FC<PropsWithChildren> {
  Header: typeof CallIntelAccordionHeader;
  Body: typeof CallIntelAccordionBody;
}

export const CallIntelAccordion: CallIntelAccordionNamespace = ({ children }) => {
  return (
    <Accordion>
      <Accordion.Item value='callIntelAccordion'>{children}</Accordion.Item>
    </Accordion>
  );
};

const CallIntelAccordionHeader: FC<{ title?: string }> = ({ title }) => {
  const { t } = useTranslation('call-intel-core');
  return <Accordion.Header title={title || t('Call Intelligence')} />;
};

interface CallIntelAccordionBodyProps
  extends Omit<CallIntelTabsProps, 'callFlags' | 'isEditMode' | 'setIsEditMode' | 'setSelectedTab' | 'selectedTab'> {
  refetchCallMetadata: () => void;
  hideFeedback?: boolean;
}

const CallIntelAccordionBody: FC<CallIntelAccordionBodyProps> = ({ refetchCallMetadata, hideFeedback, ...props }) => {
  const [isEditMode, setEditMode] = useState(false);
  const [selectedTab, setSelectedTab] = useState<CallIntelTabType>(props.defaultCallIntelTab ?? 'callSummary');

  const callFlags = callStatusUtils.getCallFlags(props.call);
  const { isFailedCall, isSkippedCall, isPoorAudio, isProcessingCall, isPrivateCall } = callFlags;

  const canShowFeedbackSection =
    !hideFeedback &&
    !isEditMode &&
    !isFailedCall &&
    !isSkippedCall &&
    !isProcessingCall &&
    !props.isLoading &&
    (!isPrivateCall || selectedTab === 'callAnalysis');

  const { showSummarySync } = useCallIntelPmsSyncConfig({
    call: props.call,
    isDemo: props.isDemoAccount,
  });

  const showFooter = canShowFeedbackSection || showSummarySync;

  return (
    <Accordion.Body css={styles.accordionBody}>
      {!isPrivateCall && (isPoorAudio || isSkippedCall || isFailedCall) && (
        <CallAnalyzeStatus {...callFlags} type='summary' styles={{ margin: 0 }} />
      )}
      <PriorityRendererItemWrapper<typeof TSSRecoveredOpportunityBanner>
        itemName={CIBannerEnum.TSS_CALL_INSIGHTS}
        groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
        closeFnProp={'onDismiss'}
      >
        <TSSRecoveredOpportunityBanner
          locationId={props.call?.locationId}
          isDemo={props.isDemoAccount}
          callInsights={props.call}
        />
      </PriorityRendererItemWrapper>
      <PriorityRendererItemWrapper<typeof TSSUnscheduledBanner>
        itemName={CIBannerEnum.TSS_CALL_INSIGHTS}
        groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
        closeFnProp={'onDismiss'}
      >
        <TSSUnscheduledBanner
          locationId={props.call?.locationId}
          isDemo={props.isDemoAccount}
          callInsights={props.call}
        />
      </PriorityRendererItemWrapper>
      <CallIntelTabs
        {...props}
        callFlags={callFlags}
        isEditMode={isEditMode}
        selectedTab={selectedTab}
        setIsEditMode={setEditMode}
        setSelectedTab={setSelectedTab}
      />
      {showFooter && !!props.callMetadata && (
        <>
          <Divider margin={0} color={theme.colors.neutral20} />
          <div css={styles.accordionFooter}>
            {showSummarySync && (
              <PriorityRendererItemWrapper<typeof CallInsightSummaryWritebackBanner>
                itemName={CIBannerEnum.PMS_WRITEBACK_CALL_INSIGHTS}
                groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
                closeFnProp={'onDismiss'}
              >
                <CallInsightSummaryWritebackBanner locationId={props.call?.locationId} />
              </PriorityRendererItemWrapper>
            )}
            <div css={styles.accordionFooterActions}>
              {canShowFeedbackSection && (
                <div css={{ maxWidth: 'max-content', marginRight: 'auto' }}>
                  <CallIntelFeedbackBanner
                    call={props.call}
                    callMetadata={props.callMetadata}
                    refetchCallMetadata={refetchCallMetadata}
                    isDemoAccount={props.isDemoAccount}
                  />
                </div>
              )}
              {showSummarySync && <SummarySyncChip isDemo={props.isDemoAccount} callRecord={props.call} />}
            </div>
          </div>
        </>
      )}
    </Accordion.Body>
  );
};

interface CallIntelTabsProps extends CallSummaryProps, CallAnalysisProps, CallTranscriptProps {
  defaultCallIntelTab?: CallIntelTabType;
  selectedTab: CallIntelTabType;
  setSelectedTab: (tab: CallIntelTabType) => void;
}

const CallIntelTabs: FC<CallIntelTabsProps> = (props) => {
  const { t } = useTranslation('callIntelInsights');
  const panelRefs = useRef<Partial<Record<CallIntelTabType, HTMLUListElement>>>({});
  const { defaultCallIntelTab, selectedTab, setSelectedTab } = props;

  const attachRef = useCallback(
    (tab: CallIntelTabType) => (el: HTMLUListElement | null) => {
      // convert null to undefined so it matches HTMLUListElement | undefined
      panelRefs.current[tab] = el ?? undefined;
    },
    []
  );

  useEffect(() => {
    if (props.highlightedCitation && !!props.highlightedCitation.text && !!props.highlightedCitation.time) {
      setSelectedTab('callTranscript');
    } else if (defaultCallIntelTab) {
      setSelectedTab(defaultCallIntelTab);
    }
  }, [props.highlightedCitation, defaultCallIntelTab, setSelectedTab]);

  useLayoutEffect(() => {
    const panel = panelRefs.current[selectedTab];

    if (panel) panel.scrollTop = 0;
  }, [selectedTab]);

  return (
    <Tabs controlledTab={selectedTab} onChange={(id) => setSelectedTab(id as CallIntelTabType)}>
      <Tabs.Bar fullWidth>
        <Tabs.Tab
          id='callSummary'
          trackingId={generateTrackingId({
            prefix: 'call-insight-panel',
            component: 'call-intel-accordion',
            context: 'summary-tab',
          })}
        >
          {t('Summary')}
        </Tabs.Tab>
        <Tabs.Tab
          id='callAnalysis'
          trackingId={generateTrackingId({
            prefix: 'call-insight-panel',
            component: 'call-intel-accordion',
            context: 'analysis-tab',
          })}
        >
          {t('Call Analysis')}
        </Tabs.Tab>
        <Tabs.Tab
          id='callTranscript'
          trackingId={generateTrackingId({
            prefix: 'call-insight-panel',
            component: 'call-intel-accordion',
            context: 'transcript-tab',
          })}
        >
          {t('Transcript')}
        </Tabs.Tab>
      </Tabs.Bar>
      <Tabs.Panel controller='callSummary'>
        <CallSummary {...props} containerStyles={styles.tabContent} />
      </Tabs.Panel>
      <Tabs.Panel controller='callAnalysis'>
        <CallAnalysis {...props} containerStyles={!props.isEditMode} />
      </Tabs.Panel>
      <Tabs.Panel controller='callTranscript'>
        <CallTranscript {...props} containerStyles={styles.tabContent} ref={attachRef('callTranscript')} />
      </Tabs.Panel>
    </Tabs>
  );
};

CallIntelAccordion.Header = CallIntelAccordionHeader;
CallIntelAccordion.Body = CallIntelAccordionBody;

const styles = {
  accordionBody: css`
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  tabContent: css`
    max-height: 288px;
  `,
  accordionFooter: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  accordionFooterActions: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
};
